import React from "react";
import { Box, Button, Icon, Text } from "@salestrak/ui";

import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { pluralize } from "#app/components/issue-view/activity/activity-utils";

import { EntityTag } from "./entity-tag";
import { SystemMarker } from "./system-marker";
import {
  EntityDetailShell,
  EntityDetailHeader,
  EntityDetailCard,
} from "./entity-detail-shell";

interface WorkflowDetailDialogProps {
  workflowId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkflowDetailDialog({
  workflowId,
  open,
  onOpenChange,
}: WorkflowDetailDialogProps) {
  const { workflowMap, stagesByWorkflow } = useWorkspaceData();
  const navigate = useAppNavigate();

  const workflow = workflowId ? workflowMap.get(workflowId) : undefined;
  const stages = workflowId ? (stagesByWorkflow.get(workflowId) ?? []) : [];

  if (!workflow) return null;

  const openBoard = () => {
    onOpenChange(false);
    navigate.push(`/workflow/${workflow.workflowId}`);
  };

  const header = (
    <EntityDetailHeader
      entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
      padded
      badge={
        workflow.isDefault ? (
          <SystemMarker kind="default" subject="workflow" />
        ) : undefined
      }
    />
  );

  const stagesCard = (
    <EntityDetailCard>
      <Box direction="column" gap="sm">
        <Box direction="row" align="center" gap="sm">
          <Icon name="layers" size="sm" color="muted" />
          <Text size="sm" variant="muted">
            {stages.length} {pluralize("Stage", { count: stages.length })}
          </Text>
          {workflow.key ? (
            <Text size="sm" variant="muted">
              · {workflow.key}
            </Text>
          ) : null}
        </Box>
        <Box direction="column" gap="xs">
          {stages.map((stage, index) => (
            <EntityTag
              key={`${stage.stageId}-${index}`}
              entity={{ kind: "stage", id: stage.stageId, stage }}
              interactive={false}
            />
          ))}
        </Box>
      </Box>
    </EntityDetailCard>
  );

  const footer = (
    <Button fullWidth shape="square" onClick={openBoard}>
      <Icon name="layout-grid" />
      Open board
    </Button>
  );

  return (
    <EntityDetailShell
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      srDescription={`${workflow.name} workflow details`}
      body={stagesCard}
      footer={footer}
    />
  );
}
